import { createSelector } from 'reselect'
import { unionBy } from 'lodash'

// Constants
import { DATE_FIELDS, FINANCIAL_FIELDS } from 'Constants'

// Utils
import { createAttribute, formatLineItem, formatResponseAttribute } from 'Utils/forms'

const DOCUMENT_DISPLAY_MAP = { folio: 'Hotel Folio', invoice: 'Invoice', receipt: 'Receipt' }

const defaultFinancialAttributes = FINANCIAL_FIELDS.map((name) => {
  return createAttribute(name, '0.00')
})
const defaultDateAttributes = ['startdate', 'enddate', 'DueDate'].map((name) => {
  return createAttribute(name, new Date().toISOString())
})
const defaultInvoiceAttributes = [
  'BankName',
  'BankAccountNumber',
  'BankRoutingNumber',
  'EmployeeIDNumber',
  'PaymentInstructions',
  'MerchantAddress',
  'PaymentTermsDiscount',
].map((name) => {
  return createAttribute(name, '')
})
const defaultOtherAttributes = ['PaymentTerms', 'PurchaseOrderNumber'].map((name) => {
  return createAttribute(name, '')
})

const defaultAttributes = [
  ...defaultFinancialAttributes,
  ...defaultDateAttributes,
  ...defaultInvoiceAttributes,
  ...defaultOtherAttributes,
].map((item, index) => ({
  ...item,
}))

const defaultPostingAttribute = (doc) => {
  return createAttribute('PostingDate', doc?.createdDate)
}

const isFinancialField = ({ name }) => {
  return FINANCIAL_FIELDS.includes(name)
}

const getFinancialValues = (attributes = []) => {
  return attributes.reduce((acc, { type, value }) => {
    return isFinancialField(type) ? { ...acc, [type.name.toLowerCase()]: value } : acc
  }, {})
}

export const getAttribute = (name, attributes = []) => {
  return attributes.find(({ type }) => {
    return type.name === name
  })
}

export const getLedgerValueFromMapping = (name, tagType, mappings = []) => {
  const mapping = mappings.find((item) => {
    return item[tagType]?.type.name === name
  })

  if (mapping) {
    return mapping[tagType]?.value
  }
}

export const getValueFromMapping = (name, tagType, mappings = []) => {
  const mapping = mappings.find((item) => {
    return item[tagType]?.type.name === name
  })

  if (mapping) {
    const value = mapping[tagType]?.value
    return { label: value, value }
  }
}

const getRiskScoreName = (doc) => {
  const riskScoreName = doc?.exceptions?.filter((exception) => {
    return exception.riskScore === 'high'
  })
  if (riskScoreName?.length > 0) {
    return 'high'
  }
  return doc.riskScore === 'pending' ? 'pending' : 'low'
}

const getRiskExceptionObj = (doc) => {
  return doc?.exceptions?.filter((exception) => {
    return exception.riskScore === 'high'
  })
}

// const hasHighRiskScore = ({ riskScore }) => {
//   return riskScore === 'high'
// }

const mapTags = (tags) => {
  return (tags || []).map((tag) => ({
    ...tag,
    label: tag.value,
  }))
}

const getDeletingExpense = ({ ledger }) => ledger.deletingExpense
const getExpenses = ({ ledger }) => ({
  expenses: ledger.expenses,
  next: ledger.next,
  previous: ledger.previous,
})
const getFetchExpenseError = ({ ledger }) => ledger.fetchExpenseError
const getFetchingExpense = ({ ledger }) => ledger.fetchingExpense
const getFetchingExpenses = ({ ledger }) => ledger.fetchingExpenses
const getSelectedDocAttributes = ({ ledger }) => ledger.selectedDocAttributes
const getSelectedDocMappings = ({ ledger }) => ledger.selectedDocMappings
const getSelectedDocOrders = ({ ledger }) => ledger.selectedDocOrders
const getSelectedDocument = ({ ledger }) => ledger.selectedDocument
const getUpdateExpenseError = ({ ledger }) => ledger.updateExpenseError
const getUpdatingExpense = ({ ledger }) => ledger.updatingExpense
const getUpdatingAttachments = ({ ledger }) => ledger.updatingAttachments
const getIsTabAttachementSectionUpload = ({ ledger }) => ledger.isTabAttachementSectionUpload
const socketDocumentCreating = ({ ledger }) => ledger.socketDocumentCreating
const getWorkFlowItems = ({ ledger }) => ledger.workFlowItems

const ledgerSelectors = {
  getDeletingExpense: createSelector([getDeletingExpense], (deletingExpense) => {
    return deletingExpense
  }),
  getSocketDocumentCreating: createSelector([socketDocumentCreating], (documentCreating) => {
    return documentCreating
  }),
  getFetchExpenseError: createSelector([getFetchExpenseError], (fetchExpenseError) => {
    return fetchExpenseError
  }),
  getExpenses: createSelector([getExpenses], ({ next, previous, expenses }) => {
    return {
      expenses: expenses.map(
        ({ documentType, merchant, orders, tagMappings, ...expense }, index) => {
          /**
           * @param {String | null} field
           * @returns {Boolean}
           * */
          function isEmptyField(field) {
            if (field === null || field === 'null' || field === 'None') {
              return true
            }
            return false
          }
          const expenseRecord = {
            ...expense,
            fileName: expense?.images?.fileName,
            currency: orders[0]?.currency?.symbol,
            documentType: DOCUMENT_DISPLAY_MAP[documentType.name],
            dueDate: getAttribute('DueDate', orders[0]?.attributes)?.value,
            documentNumber: getAttribute('DocNumber', orders[0]?.attributes)?.value,
            purchaseOrderNumber: getAttribute('PurchaseOrderNumber', orders[0]?.attributes)?.value,
            paymentTermsDiscount: getAttribute('PaymentTermsDiscount', orders[0]?.attributes)
              ?.value,
            expenseCategory:
              orders[0]?.expenseCategory?.name ||
              getLedgerValueFromMapping('ExpenseCategory', 'toTag', tagMappings)?.name ||
              merchant?.category?.expenseCategory?.name,
            glCode:
              orders[0]?.glCode?.value || getLedgerValueFromMapping('GLcode', 'toTag', tagMappings),
            ledgerIndex: index,
            merchant: merchant?.name,
            merchantImage: merchant?.image || merchant?.category.image,
            paymentStatus: orders[0]?.paymentStatus?.value.toLowerCase(),
            workflowStatus: orders[0]?.workflowStatus?.value.toLowerCase(),
            riskScore: getRiskScoreName(expense),
            ...getFinancialValues(orders[0]?.attributes),
            exceptionCount: getRiskExceptionObj(expense)?.length,
            exceptions: getRiskExceptionObj(expense),
          }

          for (let key in expenseRecord) {
            if (expenseRecord.hasOwnProperty(key) && isEmptyField(expenseRecord[key])) {
              expenseRecord[key] = ''
            }
          }

          return expenseRecord
        },
      ),
      next,
      previous,
    }
  }),
  getIntegrationExpenses: createSelector([getExpenses], ({ next, previous, expenses }) => {
    return {
      expenses: expenses.map((originalExpense, index) => {
        const { documentType, merchant, orders, tagMappings, ...expense } = originalExpense
        return {
          ...expense,
          originalExpense,
          fileName: expense?.images?.fileName,
          currency: orders[0]?.currency?.symbol,
          documentType: DOCUMENT_DISPLAY_MAP[documentType.name],
          dueDate: getAttribute('DueDate', orders[0]?.attributes)?.value,
          documentNumber: getAttribute('DocNumber', orders[0]?.attributes)?.value,
          purchaseOrderNumber: getAttribute('PurchaseOrderNumber', orders[0]?.attributes)?.value,
          paymentTermsDiscount: getAttribute('PaymentTermsDiscount', orders[0]?.attributes)?.value,
          expenseCategory:
            orders[0]?.expenseCategory?.name ||
            getLedgerValueFromMapping('ExpenseCategory', 'toTag', tagMappings)?.name ||
            merchant?.category?.expenseCategory?.name,
          glCode:
            orders[0]?.glCode?.value || getLedgerValueFromMapping('GLcode', 'toTag', tagMappings),
          ledgerIndex: index,
          merchant: merchant?.name,
          merchantImage: merchant?.image || merchant?.category?.image,
          paymentStatus: orders[0]?.paymentStatus?.value.toLowerCase(),
          workflowStatus: orders[0]?.workflowStatus?.value.toLowerCase(),
          riskScore: getRiskScoreName(expense),
          ...getFinancialValues(orders[0]?.attributes),
          exceptionCount: getRiskExceptionObj(expense)?.length,
          exceptions: getRiskExceptionObj(expense),
        }
      }),
      next,
      previous,
    }
  }),
  getIntegrationAllExpenses: createSelector([getExpenses], ({ next, previous, expenses }) => {
    return expenses
  }),
  getFetchingExpense: createSelector([getFetchingExpense], (fetchingExpense) => {
    return fetchingExpense
  }),
  getFetchingExpenses: createSelector([getFetchingExpenses], (fetchingExpenses) => {
    return fetchingExpenses
  }),
  getSelectedDocument: createSelector(
    [
      getSelectedDocument,
      getSelectedDocAttributes,
      getSelectedDocMappings,
      getSelectedDocOrders,
      getWorkFlowItems,
    ],
    (doc, attributes, mappings, orders, workFlowItems) => {
      if (!doc || !Object.keys(doc).length) {
        return {}
      }

      // Capitalize map document type names to labels and sort the attributes for display.
      const documentType = {
        ...doc.documentType,
        label: DOCUMENT_DISPLAY_MAP[doc.documentType.name],
      }

      // Temporary - Add any missing fields
      const withDefaults = unionBy(
        [...attributes],
        [...defaultAttributes, defaultPostingAttribute(doc)],
        'type.name',
      )

      const sortedAttributes = withDefaults.sort((attributeA, attributeB) => {
        return attributeA.type.name.localeCompare(attributeB.type.name)
      })
      const sortedFinancialFields = sortedAttributes.sort((a, b) => {
        return FINANCIAL_FIELDS.indexOf(a.type.name) - FINANCIAL_FIELDS.indexOf(b.type.name)
      })
      const withTypeFlags = sortedFinancialFields.map((attribute) => {
        const isFinancialField = FINANCIAL_FIELDS.includes(attribute.type.name)
        const isDateField = DATE_FIELDS.includes()
        return { ...attribute, isDateField, isFinancialField }
      })

      const formattedAttributes = withTypeFlags.map((attribute) => {
        return formatResponseAttribute(attribute)
      })

      const formattedLineItems = orders.lineItems.map((lineItem) => {
        return formatLineItem(lineItem)
      })

      // const riskScoreName = doc?.exceptions?.filter((exception) => {
      //   return exception.riskScore === 'high'
      // })

      return {
        attributes: formattedAttributes,
        currency: orders.currency,
        documentType: documentType,
        exceptions: doc.exceptions,
        expenseCategory:
          orders?.expenseCategory ||
          getValueFromMapping('ExpenseCategory', 'toTag', mappings) ||
          doc?.merchant?.category?.expenseCategory,
        glCode: orders.glCode || getValueFromMapping('GLcode', 'toTag', mappings),
        guid: doc.guid,
        images: doc.images,
        jobCodes: mapTags(orders.jobCodes),
        lineItems: formattedLineItems,
        workFlowItems: workFlowItems || [],
        merchant: doc.merchant,
        isBankMatched: doc.isBankMatched,
        matchedTransaction: doc.matchedTransaction,
        notes: orders.notes,
        orderNumber: orders.orderNumber,
        purchaseOrderNumber: orders.purchaseOrderNumber,
        matchedPurchaseOrder: doc.matchedPurchaseOrder,
        otherAttachments: orders.otherAttachments,
        paymentStatus: orders.paymentStatus,
        workflowStatus: orders.workflowStatus,
        paymentType: orders.paymentType,
        purchaseOrders: orders.purchaseOrders,
        riskScore: getRiskScoreName(doc), //riskScoreName?.length > 0 ? 'high' : 'low',
        shippingDocuments: orders.shippingDocuments,
        source: doc.source,
        tags: mapTags(orders.tags),
        transactionDate: doc.transactionDate,
        createdDate: doc.createdDate,
      }
    },
  ),
  getUpdateExpenseError: createSelector([getUpdateExpenseError], (updateExpenseError) => {
    return updateExpenseError
  }),
  getUpdatingExpense: createSelector([getUpdatingExpense], (updatingExpense) => {
    return updatingExpense
  }),
  getUpdatingAttachments: createSelector([getUpdatingAttachments], (updatingAttachments) => {
    return updatingAttachments
  }),
  getIsTabAttachementSectionUpload: createSelector(
    [getIsTabAttachementSectionUpload],
    (isTabAttachementSectionUpload) => {
      return isTabAttachementSectionUpload
    },
  ),
}

export default ledgerSelectors
